import { Log, User, UserManager, UserManagerSettings } from "oidc-client-ts";

type AuthenticationEnvironment = {
  CLIENT_SCOPE: string;
  CLIENT_ID: string;
  CLIENT_ROOT: string;
  STS_AUTHORITY: string;
};

const getEnvironment = (): AuthenticationEnvironment => ({
  CLIENT_SCOPE: window?._env_?.REACT_APP_AUTH_CLIENT_SCOPE || import.meta.env.REACT_APP_AUTH_CLIENT_SCOPE || "openid profile email",
  CLIENT_ID: window?._env_?.REACT_APP_AUTH_CLIENT_ID || import.meta.env.REACT_APP_AUTH_CLIENT_ID!,
  CLIENT_ROOT: window?._env_?.REACT_APP_AUTH_CLIENT_ROOT || import.meta.env.REACT_APP_AUTH_CLIENT_ROOT || window.location.origin,
  STS_AUTHORITY: window?._env_?.REACT_APP_STS_AUTHORITY || import.meta.env.REACT_APP_STS_AUTHORITY!,
});

type RenewCallback =
  | ((
      success: boolean,
      token?: string,
      email?: string,
      username?: string 
    ) => void)
  | undefined;

export class AuthenticationService {
  public userManager: UserManager;
  private renewCallback: RenewCallback;
  private user: User | null;
  private readonly settings: UserManagerSettings;
  private callbackInProgress: boolean = false;
  private lastCallbackAttempt: number = 0;
  private isRedirectingToLogin: boolean = false;
  private renewalInProgress: Promise<void> | null = null;

  private constructor() {
    this.settings = {
      authority: getEnvironment().STS_AUTHORITY,
      client_id: getEnvironment().CLIENT_ID,
      redirect_uri: `${getEnvironment().CLIENT_ROOT}/signin-callback`,
      silent_redirect_uri: `${getEnvironment().CLIENT_ROOT}/silent-renew`,
      post_logout_redirect_uri: `${getEnvironment().CLIENT_ROOT}`,
      response_type: "code",
      scope: getEnvironment().CLIENT_SCOPE,
      response_mode: "query",
      automaticSilentRenew: true,
      includeIdTokenInSilentRenew: true,
      loadUserInfo: false,
      accessTokenExpiringNotificationTimeInSeconds: 60, // 60 seconds before expiry
      silentRequestTimeoutInSeconds: 10, // 10 seconds timeout for silent renewal
    };
    this.user = null;
    this.userManager = new UserManager(this.settings);

    this.userManager.events.addAccessTokenExpired(() => {
      console.log("[AuthService] Access token expired, attempting renewal...");
      this.renewToken();
    });

    this.userManager.events.addSilentRenewError((error) => {
      console.error("[AuthService] Silent renew error:", error);
      
      // Prevent multiple redirects
      if (this.isRedirectingToLogin) {
        console.log("[AuthService] Already redirecting to login, skipping...");
        return;
      }
      
      // If silent renewal fails, check if we need to re-authenticate
      this.userManager.getUser().then((user) => {
        if (!user || user.expired) {
          console.log("[AuthService] User session invalid, redirecting to login...");
          this.isRedirectingToLogin = true;
          
          // Clear stale state and redirect to login
          this.userManager.removeUser().then(() => {
            this.user = null;
            // Redirect to login, preserving the current page
            this.login(window.location.pathname + window.location.search);
          });
        }
      });
    });

    this.userManager.events.addUserLoaded((user) => {
      console.log("[AuthService] User loaded successfully");
      this.user = user;
      if (this.renewCallback) {
        this.renewCallback(
          user !== undefined,
          this.user?.access_token,
          this.user?.profile.email,
          this.user?.profile.preferred_username
        );
      }
    });

    this.userManager.events.addUserUnloaded(() => {
      console.log("[AuthService] User unloaded");
      this.user = null;
    });

    Log.setLogger(console);
    Log.setLevel(import.meta.env.DEV ? Log.INFO : Log.WARN);
  }

  public static readonly Instance = new AuthenticationService();

  public login(redirectUri: string): Promise<void> {
    return this.userManager.signinRedirect({
      extraQueryParams: { kc_idp_hint: "oidc" },
      state: {
        redirectUri,
      },
    });
  }

  public renewToken(): Promise<void> {
    if (this.renewalInProgress) {
      return this.renewalInProgress;
    }

    this.renewalInProgress = this.performRenewal().finally(() => {
      this.renewalInProgress = null;
    });

    return this.renewalInProgress;
  }

  private async performRenewal(): Promise<void> {
    try {
      console.log("[AuthService] Attempting silent token renewal...");
      
      // Ensure settings are still configured
      if (!this.settings.authority) {
        console.error("[AuthService] Settings authority is missing, reinitializing UserManager");
        this.settings.authority = getEnvironment().STS_AUTHORITY;
        this.userManager = new UserManager(this.settings);
      }
      
      const user = await this.userManager.signinSilent({
        redirect_uri: this.settings.silent_redirect_uri,
      });
      
      if (user) {
        console.log("[AuthService] Silent renewal successful");
        this.user = user;
        if (this.renewCallback) {
          this.renewCallback(
            true,
            user.access_token,
            user.profile.email,
            user.profile.preferred_username
          );
        }
      } else {
        console.warn("[AuthService] Silent renewal returned null user");
        throw new Error("Silent renewal returned null user");
      }
    } catch (error) {
      console.error("[AuthService] Silent renewal failed:", error);
      
      // Try to get existing user first
      const existingUser = await this.userManager.getUser();
      if (existingUser && !existingUser.expired) {
        console.log("[AuthService] Using existing valid user session");
        this.user = existingUser;
        return;
      }
      
      // If no valid user, clear session and optionally redirect to login
      console.log("[AuthService] No valid user session found");
      await this.userManager.removeUser();
      this.user = null;
      
      if (this.renewCallback) {
        this.renewCallback(false, undefined, undefined, undefined);
      }
      
      // Optionally throw the error for the caller to handle
      throw error;
    }
  }

  public getUserToken(): string | undefined {
    return this.user?.access_token;
  }

  public email(): string | undefined {
    return this.user?.profile.email;
  }

  public async getUser(): Promise<{
    token: string | undefined;
    email: string | undefined;
    username: string | undefined;
  }> {
    const user = await this.userManager.getUser();

    return {
      token: user?.access_token,
      email: user?.profile.email,
      username: user?.profile.preferred_username,
    };
  }

  /**
   * Gets a valid, non-expired access token.
   * If no user exists, throws an error (login required).
   * If token is expired, attempts renewal.
   * @returns Promise<string> - A valid access token
   * @throws Error if unable to obtain a valid token
   */
  public async getValidToken(): Promise<string> {
    try {
      const currentUser = await this.userManager.getUser();
      
      // No user exists - need to login first
      if (!currentUser) {
        console.log("[AuthService] No user found, redirecting to login...");
        // Don't redirect automatically here - let the calling code handle it
        throw new Error("No authenticated user - login required");
      }
      
      // User exists but token is expired - try renewal
      if (currentUser.expired) {
        console.log("[AuthService] Token expired, attempting renewal...");
        try {
          await this.renewToken();
          
          // Get fresh user after renewal
          const renewedUser = await this.userManager.getUser();
          if (!renewedUser || renewedUser.expired || !renewedUser.access_token) {
            throw new Error("Token renewal failed");
          }
          
          return renewedUser.access_token;
        } catch (renewError) {
          console.error("[AuthService] Renewal failed, login required:", renewError);
          throw new Error("Token renewal failed - login required");
        }
      }
      
      // Token is valid
      if (!currentUser.access_token) {
        throw new Error("No access token available");
      }
      
      return currentUser.access_token;
    } catch (error) {
      console.error("[AuthService] Failed to get valid token:", error);
      throw error;
    }
  }

  public logout(): Promise<void> {
    return this.userManager.signoutRedirect();
  }

  public async acceptCallback(): Promise<string | undefined> {
    const now = Date.now();
    
    // Prevent multiple concurrent callback processing
    if (this.callbackInProgress) {
      console.warn("[AuthService] Callback processing already in progress, skipping duplicate request");
      throw new Error("Callback processing already in progress");
    }
    
    // Prevent rapid successive callback attempts (within 5 seconds)
    if (now - this.lastCallbackAttempt < 5000) {
      console.warn("[AuthService] Recent callback attempt detected, preventing duplicate processing");
      throw new Error("Recent callback attempt detected - possible duplicate");
    }
    
    this.callbackInProgress = true;
    this.lastCallbackAttempt = now;
    
    try {
      console.log("[AuthService] Starting signin redirect callback processing...");

      // If the user already has a valid session (e.g. the page was reloaded
      // mid-callback after the code exchange already succeeded), skip
      // reprocessing — reusing a consumed code would give AuthenticationCodeError.
      const existingUser = await this.userManager.getUser();
      if (existingUser && !existingUser.expired && existingUser.access_token) {
        console.log("[AuthService] Valid session already exists, skipping callback reprocessing");
        this.user = existingUser;
        return undefined; // caller falls back to returnUrl ('/')
      }

      // Do NOT call removeUser() here: it would destroy a session that was
      // established by an earlier processing of this same callback before reload.
      const user = await this.userManager.signinRedirectCallback();
      
      if (user) {
        console.log("[AuthService] Callback processed successfully, user authenticated");
        this.user = user;
        
        // Trigger user loaded event manually if needed
        if (this.renewCallback) {
          this.renewCallback(
            true,
            user.access_token,
            user.profile.email,
            user.profile.preferred_username
          );
        }

        // Return the original redirect URI stored in OIDC state so the caller
        // can navigate back to the page the user was on before the auth redirect.
        return (user.state as { redirectUri?: string } | undefined)?.redirectUri;
      } else {
        console.warn("[AuthService] Callback processed but no user returned");
        throw new Error("Callback processing returned no user");
      }
      
    } catch (error: any) {
      console.error("[AuthService] Error during signin callback:", error);
      
      // Enhanced error handling for specific error types
      if (error?.message?.includes("Code not valid") || error?.error === "invalid_grant") {
        console.error("[AuthService] Authorization code validation failed - possible code reuse or expiration");
        
        // Clear any corrupted state
        await this.userManager.removeUser();
        this.user = null;
        
        // Create a more specific error for the UI
        const enhancedError = new Error("Authentication failed: The authorization code has expired or been used already. Please try signing in again.");
        enhancedError.name = "AuthenticationCodeError";
        throw enhancedError;
      }
      
      // Re-throw other errors as-is
      throw error;
      
    } finally {
      this.callbackInProgress = false;
    }
  }

  public addRenewCallback(cb: RenewCallback) {
    if (this.renewCallback) {
      console.error("[AuthService] Renew callback already assigned");
      return;
    }
    this.renewCallback = cb;
  }

  public removeRenewCallback() {
    this.renewCallback = undefined;
  }

  public isCallbackInProgress(): boolean {
    return this.callbackInProgress;
  }

  public getLastCallbackAttempt(): number {
    return this.lastCallbackAttempt;
  }

  public async isAuthenticated(): Promise<boolean> {
    try {
      const user = await this.userManager.getUser();
      return user !== null && !user.expired;
    } catch (error) {
      console.error("[AuthService] Error checking authentication status:", error);
      return false;
    }
  }

  public async clearAuthenticationState(): Promise<void> {
    try {
      console.log("[AuthService] Clearing authentication state...");
      await this.userManager.removeUser();
      this.user = null;
      this.callbackInProgress = false;
      this.lastCallbackAttempt = 0;
      this.isRedirectingToLogin = false;
      console.log("[AuthService] Authentication state cleared");
    } catch (error) {
      console.error("[AuthService] Error clearing authentication state:", error);
      throw error;
    }
  }
}
